import React from 'react'
import { connect } from 'react-redux'
import history from 'browserHistory'
import { compose } from 'recompose'
import Mousetrap from 'mousetrap'
import DesktopHelpers from 'helpers/DesktopHelpers'
import {
  setActiveModal,
  toggleFocusMode,
  toggleChannelsList,
  uncollapseSidebar,
} from 'data/ui/actions'
import {
  isFocusModeEnabled,
  isSidebarChannelsListActive,
  isSidebarCollapsed,
  isMetaPanelOpen,
} from 'data/ui/selectors'
import { activate as activateSearch, exit as exitSearch } from 'data/search'
import { actions as NotesActions } from 'data/notes'
import {
  trackInterfaceShortcut,
  trackOpenQuickSwitch,
} from 'data/analytics/actions'
import { withNoteMetaPanelControls } from 'routes/utils/withNoteMetaPanelControls'

const shortcutsBindings = {
  ENTER_SEARCH: DesktopHelpers.isMac() ? ['command+shift+k'] : ['ctrl+shift+k'],
  ENTER_SWITCH: DesktopHelpers.isMac()
    ? ['command+k', DesktopHelpers.isAppDesktop() ? 'command+t' : '']
    : ['ctrl+k', DesktopHelpers.isAppDesktop() ? 'ctrl+t' : ''],
  ESCAPE: ['escape'],
  CREATE_NOTE: DesktopHelpers.isAppDesktop() ? ['command+n'] : ['alt+n'],
  NAVIGATE_BACKWARD: ['command+left'],
  NAVIGATE_FORWARD: ['command+right'],
  TOGGLE_CHANNELS_LIST: DesktopHelpers.isMac()
    ? ['command+shift+e']
    : ['ctrl+shift+e'],
}

function getSelectedText() {
  let text = ''
  // eslint-disable-next-line eqeqeq
  if (typeof window.getSelection != 'undefined') {
    text = window.getSelection().toString()
  } else if (
    // eslint-disable-next-line eqeqeq
    typeof document.selection != 'undefined' &&
    document.selection.type === 'Text'
  ) {
    text = document.selection.createRange().text
  }
  return text
}

const isInputActive = event => {
  let target = event.target || event.srcElement
  let isInput = false
  while (target) {
    const { tagName, contentEditable } = target
    if (
      contentEditable === 'true' ||
      tagName === 'INPUT' ||
      tagName === 'SELECT' ||
      tagName === 'TEXTAREA'
    ) {
      isInput = true
      break
    }
    target = target.parentNode
  }
  return isInput
}

function Shortcuts(props) {
  // saving these props as references so shortcut callbacks have last value
  const propsRef = React.useRef()
  propsRef.current = props

  React.useEffect(
    () => {
      const mousetrap = new Mousetrap()

      function openSearch(event) {
        event.preventDefault()
        propsRef.current.trackOpenQuickSwitch({ source: 'shortcut' })
        propsRef.current.activateSearch()
        propsRef.current.trackInterfaceShortcut('search')
      }

      mousetrap.stopCallback = (event, element, combo) => {
        // Prevent opening QuickSwitch is event has been prevented by inline toolbar for instance
        if (shortcutsBindings.ENTER_SWITCH.includes(combo)) {
          return event.defaultPrevented || getSelectedText().length > 0
        }
        return false
      }

      mousetrap.bind(shortcutsBindings.ENTER_SEARCH, openSearch)

      mousetrap.bind(shortcutsBindings.ENTER_SWITCH, openSearch)

      mousetrap.bind(shortcutsBindings.ESCAPE, event => {
        if (event.defaultPrevented) {
          return
        }
        // Close note meta  panel
        if (propsRef.current.noteMetaPanelIsOpen) {
          propsRef.current.hideNoteMetaPanel()
        }

        // Exit focus mode
        if (propsRef.current.isFocusModeEnabled) {
          propsRef.current.toggleFocusMode()
        }

        // Close sidebar channels list panel
        if (propsRef.current.isSidebarChannelsListActive) {
          propsRef.current.toggleChannelsList()
        }

        // Exit focus mode with new sidebar layout
        if (propsRef.current.isSidebarCollapsed) {
          propsRef.current.uncollapseSidebar()
        }

        // Close search
        propsRef.current.exitSearch()
        propsRef.current.trackInterfaceShortcut('escape')
      })

      mousetrap.bind(shortcutsBindings.CREATE_NOTE, event => {
        if (!isInputActive(event)) {
          propsRef.current.onCreateNote()
          propsRef.current.trackInterfaceShortcut('create note')
        }
      })

      mousetrap.bind(shortcutsBindings.NAVIGATE_BACKWARD, event => {
        if (!isInputActive(event)) {
          event.preventDefault()
          event.stopPropagation()
          history.goBack()
          propsRef.current.trackInterfaceShortcut('navigateBackward')
        }
      })
      mousetrap.bind(shortcutsBindings.NAVIGATE_FORWARD, event => {
        if (!isInputActive(event)) {
          event.preventDefault()
          event.stopPropagation()
          history.goForward()
          propsRef.current.trackInterfaceShortcut('navigateForward')
        }
      })

      mousetrap.bind(shortcutsBindings.TOGGLE_CHANNELS_LIST, event =>
        propsRef.current.toggleChannelsList()
      )

      return () => mousetrap.reset()
    },
    [props.useNewSearch]
  )

  return false // just a side effect
}

const mapDispatchToProps = {
  onCreateNote: NotesActions.createNote,
  setActiveModal,
  toggleFocusMode,
  trackInterfaceShortcut,
  trackOpenQuickSwitch,
  activateSearch,
  exitSearch,
  toggleChannelsList,
  uncollapseSidebar,
}

export default compose(
  withNoteMetaPanelControls,
  connect(
    state => ({
      noteMetaPanelIsOpen: isMetaPanelOpen(state),
      isFocusModeEnabled: isFocusModeEnabled(state),
      isSidebarCollapsed: isSidebarCollapsed(state),
      isSidebarChannelsListActive: isSidebarChannelsListActive(state),
    }),
    mapDispatchToProps
  )
)(React.memo(Shortcuts))
